/**
 * The one-time Partner Terms modal for an EXISTING organisation (owner
 * decision 2026-09-26). Shown when whoami says `terms_required`: the org was
 * created before the click-through (migration 069) or the Terms version has
 * moved since it accepted.
 *
 *   * Owner / admin: blocking. It cannot be dismissed; the only ways out are
 *     accepting or signing out. The widget and lead delivery keep running
 *     meanwhile — nothing on that path reads acceptance.
 *   * Every other role: informational and dismissible, once per session.
 *     They cannot bind the organisation, and locking an operator out of the
 *     inbox until an owner signs in would stop leads being worked.
 *
 * New organisations never see this: OrgSetup's checkbox accepts at creation.
 *
 * The same modal opens from Organisation → Partner Terms → "Review and
 * accept" (`useTermsReview().open()`), for an owner or admin who got there
 * without it (an agent-token refusal, a whoami that failed open). Opened that
 * way and not owed, it can be closed with "Not now".

 */
import { createContext, useCallback, useContext, useId, useMemo, useState, type ReactNode } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { ErrorNotice, Modal } from '../ui/kit';
import { useMutation } from '../ui/hooks';
import { CURRENT_TERMS, TERMS_PATH, legalVersionLabel } from './current';

export function TermsLinks() {
  return (
    <a href={TERMS_PATH} target="_blank" rel="noreferrer">
      FinderlyFix Partner Terms
    </a>
  );
}

interface TermsReview {
  /** Open the terms modal on demand (Organisation → Partner Terms). */
  open: () => void;
  /** Bumped after every acceptance, so readers of GET /partner/terms re-read. */
  acceptedCount: number;
}

const TermsReviewContext = createContext<TermsReview & { requested: boolean; close: () => void; accepted: () => void }>({
  open: () => {},
  acceptedCount: 0,
  requested: false,
  close: () => {},
  accepted: () => {},
});

export const useTermsReview = (): TermsReview => useContext(TermsReviewContext);

/** Holds "the partner asked to review the Terms" and renders the modal over `children`. */
export function TermsReviewProvider({ children }: { children: ReactNode }) {
  const [requested, setRequested] = useState(false);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const open = useCallback(() => setRequested(true), []);
  const close = useCallback(() => setRequested(false), []);
  const accepted = useCallback(() => {
    setRequested(false);
    setAcceptedCount((n) => n + 1);
  }, []);
  const value = useMemo(() => ({ open, acceptedCount, requested, close, accepted }), [open, acceptedCount, requested, close, accepted]);
  return (
    <TermsReviewContext.Provider value={value}>
      {children}
      <TermsGate />
    </TermsReviewContext.Provider>
  );
}

export function TermsGate() {
  const { api, org, role, termsRequired, markTermsAccepted, signOut } = useSession();
  const review = useContext(TermsReviewContext);
  const [ticked, setTicked] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const accept = useMutation(() => api.acceptTerms(CURRENT_TERMS));
  const canAccept = can(role, 'acceptTerms');
  const checkboxId = useId();
  const showAcceptor = canAccept && (termsRequired || review.requested);

  if (!canAccept) {
    if (!termsRequired || dismissed) return null;
    return (
      <Modal
        open
        title="Updated Partner Terms"
        onClose={() => setDismissed(true)}
        footer={
          <button type="button" className="btn btn-primary" onClick={() => setDismissed(true)}>
            OK
          </button>
        }
      >
        <p>
          An owner or admin of {org?.display_name ?? 'your organisation'} needs to accept the <TermsLinks />. You can keep working in the
          meantime.
        </p>
      </Modal>
    );
  }

  if (!showAcceptor) return null;
  // Owed: blocking, the only ways out are accepting or signing out. Asked for
  // from Organisation while nothing is owed: "Not now" closes it.
  const blocking = termsRequired;

  return (
    <Modal
      open
      dismissible={!blocking}
      title="Review the Partner Terms"
      onClose={review.close}
      footer={
        <>
          {blocking ? (
            <button type="button" className="btn btn-ghost" onClick={() => void signOut()}>
              Sign out
            </button>
          ) : (
            <button type="button" className="btn btn-ghost" onClick={review.close}>
              Not now
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ticked || accept.pending}
            onClick={async () => {
              if (await accept.run()) {
                markTermsAccepted();
                review.accepted();
                setTicked(false);
              }
            }}
          >
            {accept.pending ? 'Saving…' : 'Accept and continue'}
          </button>
        </>
      }
    >
      <div className="stack-sm">
        <p>
          To keep using the portal, {org?.display_name ?? 'your organisation'} needs to accept the current <TermsLinks />. Your widget and lead
          delivery keep working while you read them.
        </p>
        <div className="check">
          <input id={checkboxId} type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
          <label htmlFor={checkboxId}>
            I have read and agree to the Partner Terms on behalf of {org?.display_name ?? 'my organisation'}, and I
            am authorised to do so.
          </label>
        </div>
        <p className="small muted">Version of {legalVersionLabel(CURRENT_TERMS.terms_version)}</p>
        <ErrorNotice error={accept.error} />
      </div>
    </Modal>
  );
}
